import { useSearchStore } from '../../stores/search-store.js';
import { useLandProposalStore } from '../../stores/land-proposal-store.js';
import { useBarClause } from '../../hooks/useBarClause.js';
import { useSearch } from '../../hooks/useSearch.js';

// ─── FilterBar Props ──────────────────────────────────────────────────────────

interface FilterBarProps {
  /** Called when the "Generate Land Base" button is clicked. */
  onGenerateLandBase?: () => void;
  /** Whether to show the "Generate Land Base" button (only when deck has entries). */
  showLandButton?: boolean;
  /** Whether the color identity is colorless (disables the land button). */
  isColorless?: boolean;
  /** Whether the land modal is currently open (highlights the button). */
  isLandModalOpen?: boolean;
}

const TYPE_OPTIONS = [
  { value: '', label: 'Any Type' },
  { value: 'creature', label: 'Creature' },
  { value: 'instant', label: 'Instant' },
  { value: 'sorcery', label: 'Sorcery' },
  { value: 'enchantment', label: 'Enchantment' },
  { value: 'artifact', label: 'Artifact' },
  { value: 'planeswalker', label: 'Planeswalker' },
  { value: 'land', label: 'Land' },
];

const RARITY_OPTIONS = [
  { value: '', label: 'Any Rarity' },
  { value: 'c', label: 'Common' },
  { value: 'u', label: 'Uncommon' },
  { value: 'r', label: 'Rare' },
  { value: 'm', label: 'Mythic' },
];

export function FilterBar({
  onGenerateLandBase,
  showLandButton = false,
  isColorless = false,
  isLandModalOpen = false,
}: FilterBarProps = {}) {
  const filters = useSearchStore((s) => s.filters);
  const results = useSearchStore((s) => s.results);
  const isAdvancedOpen = useSearchStore((s) => s.isAdvancedOpen);
  const setFilters = useSearchStore((s) => s.setFilters);
  const setAdvancedOpen = useSearchStore((s) => s.setAdvancedOpen);
  const hasPendingProposal = useLandProposalStore((s) => s.pendingProposal !== null);
  const { writeClause } = useBarClause();
  const { execute } = useSearch();

  const reSearch = () => { if (results) void execute(1); };

  const handleType = (value: string) => {
    setFilters({ type: value || undefined });
    writeClause('type:', value || null);
    reSearch();
  };

  const handleRarity = (value: string) => {
    setFilters({ rarity: value || undefined });
    writeClause('r:', value || null);
    reSearch();
  };

  const handlePrice = (value: string) => {
    const num = value ? parseFloat(value) : undefined;
    setFilters({ maxPrice: num });
    writeClause('usd<=', num !== undefined ? String(num) : null);
    reSearch();
  };

  const selectClass =
    'bg-gray-800 border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-gray-300 focus:outline-none focus:border-orange-500 transition-colors cursor-pointer';

  return (
    <div className="flex flex-wrap gap-2 items-center">
      <select
        value={filters.type ?? ''}
        onChange={(e) => handleType(e.target.value)}
        className={selectClass}
      >
        {TYPE_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>

      <select
        value={filters.rarity ?? ''}
        onChange={(e) => handleRarity(e.target.value)}
        className={selectClass}
      >
        {RARITY_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>

      <input
        type="number"
        min={0}
        step={0.25}
        value={filters.maxPrice ?? ''}
        onChange={(e) => handlePrice(e.target.value)}
        placeholder="Max $"
        className="bg-gray-800 border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-gray-300 placeholder-gray-600 focus:outline-none focus:border-orange-500 w-20"
      />

      <button
        onClick={() => setAdvancedOpen(!isAdvancedOpen)}
        className={`px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
          isAdvancedOpen
            ? 'bg-orange-600/20 border-orange-700/40 text-orange-400'
            : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500'
        }`}
      >
        Advanced {isAdvancedOpen ? '\u25B4' : '\u25BE'}
      </button>

      {showLandButton && (
        <button
          type="button"
          onClick={isColorless ? undefined : onGenerateLandBase}
          disabled={isColorless}
          title={isColorless ? 'Colorless commander — no color identity to generate from' : undefined}
          className={`px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
            isLandModalOpen || hasPendingProposal
              ? 'bg-orange-600/20 border-orange-700/40 text-orange-400'
              : isColorless
                ? 'bg-gray-800 border-gray-700 text-gray-600 cursor-not-allowed'
                : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500'
          }`}
          aria-label="Generate land base"
        >
          Generate Land Base
        </button>
      )}
    </div>
  );
}
